import { useEffect } from 'react';
import { FeaturebaseProvider, useFeaturebase } from 'featurebase-js/react';
import GlobalStyles from '@mui/material/GlobalStyles';
import { useUser } from 'common/context/UserContext';
import { useFeaturebaseJwtQuery } from 'api/queries/featurebase';
import {
  DRAWER_BODY_CLASS_COLLAPSED,
  DRAWER_BODY_CLASS_EXPANDED,
  DRAWER_HEIGHT_COLLAPSED,
  DRAWER_HEIGHT_EXPANDED,
} from 'pages/ItineraryBuilder/constants';

const FEATUREBASE_LAUNCHER_HEIGHT = 64;
// Featurebase sizes the panel as `min(708px, 100% - 104px)` anchored to the viewport, where the
// 104px assumes the default `bottom: 84px`. When we raise `bottom` to clear the drawer we must cap
// max-height by the same amount, preserving the ~20px top breathing room the SDK bakes in.
const FEATUREBASE_TOP_MARGIN = 20;
const LAUNCHER_SELECTOR = '.featurebase-messenger-launcher-frame-wrapper';
const NOTIFICATION_SELECTOR = '.featurebase-notification-frame';
const WIDGET_SELECTOR = '.featurebase-messenger-frame-wrapper';
const ALL_FRAME_SELECTORS = `${LAUNCHER_SELECTOR}, ${NOTIFICATION_SELECTOR}, ${WIDGET_SELECTOR}`;

function drawerSelectors(drawerClass, ...selectors) {
  return selectors.map(s => `body.${drawerClass} ${s}`).join(', ');
}

const featurebaseStyles = {
  '@keyframes featurebase-fade-in': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  [ALL_FRAME_SELECTORS]: {
    zIndex: '1199 !important',
    filter: 'drop-shadow(0 1px 3px rgba(0, 0, 0, 0.4)) !important',
    animation: 'featurebase-fade-in 400ms ease-out both !important',
    transition: 'bottom 225ms ease, max-height 225ms ease !important',
  },
  [drawerSelectors(DRAWER_BODY_CLASS_COLLAPSED, LAUNCHER_SELECTOR, NOTIFICATION_SELECTOR)]: {
    bottom: `${DRAWER_HEIGHT_COLLAPSED}px !important`,
  },
  [drawerSelectors(DRAWER_BODY_CLASS_COLLAPSED, WIDGET_SELECTOR)]: {
    bottom: `${DRAWER_HEIGHT_COLLAPSED + FEATUREBASE_LAUNCHER_HEIGHT}px !important`,
    maxHeight: `calc(100% - ${DRAWER_HEIGHT_COLLAPSED + FEATUREBASE_LAUNCHER_HEIGHT + FEATUREBASE_TOP_MARGIN}px) !important`,
  },
  [drawerSelectors(DRAWER_BODY_CLASS_EXPANDED, LAUNCHER_SELECTOR, NOTIFICATION_SELECTOR)]: {
    bottom: `${DRAWER_HEIGHT_EXPANDED}px !important`,
  },
  [drawerSelectors(DRAWER_BODY_CLASS_EXPANDED, WIDGET_SELECTOR)]: {
    bottom: `${DRAWER_HEIGHT_EXPANDED + FEATUREBASE_LAUNCHER_HEIGHT}px !important`,
    maxHeight: `calc(100% - ${DRAWER_HEIGHT_EXPANDED + FEATUREBASE_LAUNCHER_HEIGHT + FEATUREBASE_TOP_MARGIN}px) !important`,
  },
};

function UnreadTitleAlert() {
  const { unreadCount } = useFeaturebase();

  useEffect(() => {
    if (unreadCount > 0) {
      const base = document.title.replace(/^\(\d+\)\s*/, '');
      document.title = `(${unreadCount}) ${base}`;
    } else {
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    }
    return () => {
      document.title = document.title.replace(/^\(\d+\)\s*/, '');
    };
  }, [unreadCount]);

  return null;
}

function FeaturebaseMessenger() {
  const { user } = useUser();
  const { data: featurebaseJwt = null } = useFeaturebaseJwtQuery(user?.userId);

  return (
    <>
      <GlobalStyles styles={featurebaseStyles} />
      {featurebaseJwt && (
        <FeaturebaseProvider
          appId={process.env.REACT_APP_FEATUREBASE_APP_ID}
          featurebaseJwt={featurebaseJwt}
          theme={window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'}
          language="en"
        >
          <UnreadTitleAlert />
        </FeaturebaseProvider>
      )}
    </>
  );
}

export default FeaturebaseMessenger;
